/**
 * PlaceVoxel — T-1.3.5.
 *
 * The first real Command: insert a single voxel into `state.voxels` on
 * `apply`, delete it on `revert`.
 *
 * Why structural typing on `state`:
 *   - The Command interface (Command.ts) accepts the narrowest host
 *     state that exposes `voxels`. PlaceVoxel only needs `voxels`, so
 *     it accepts just `{ voxels }`. This keeps the unit test honest
 *     (see tests/unit/place-voxel.test.ts uses a plain Map-shaped stub)
 *     and avoids circular deps between commands and the live store.
 *
 * What this Command does NOT do (out-of-scope for T-1.3.5):
 *   - It does not render a mesh. M3's InstancedMesh will sync from
 *     `state.voxels` on its own — see PRD FR-202.
 *   - It does not check for conflicts. Conflict detection is a render-
 *     layer hint (see checkConflict.ts); PlaceVoxel is the source of
 *     truth and will overwrite on duplicate coord if the caller asks.
 *     MainCanvas gates drop on `!checkConflict` before applying.
 *   - It does not assign id/color/label. Those are generated by the
 *     caller (MainCanvas) and passed in as `props`.
 *
 * Idempotence:
 *   - `apply` on an already-occupied coord overwrites the previous
 *     entry. This is by design — the caller is responsible for
 *     deciding what "place" means (single, replace, reject).
 *   - `revert` on an absent coord is a safe no-op. This makes
 *     undo-after-replace semantics well-defined even if the second
 *     `apply` rewrote a different props value.
 */

import type { VoxelCoord, VoxelProps } from '../state/types';
import type { Command } from './Command';

export class PlaceVoxel implements Command {
  readonly type = 'place';

  constructor(
    private readonly coord: VoxelCoord,
    private readonly props: VoxelProps,
  ) {}

  apply(state: { voxels: Map<VoxelCoord, VoxelProps> }): void {
    state.voxels.set(this.coord, this.props);
  }

  revert(state: { voxels: Map<VoxelCoord, VoxelProps> }): void {
    state.voxels.delete(this.coord);
  }
}
